import { Injectable } from '@angular/core';

/**
 * IndexedDB-backed store for basemap tiles the user has already loaded.
 *
 * Satellite tiles are large, immutable for months at a time, and field workers
 * often revisit the same few sites on poor connections — so caching them
 * locally is worth far more than it costs. Every operation degrades to a no-op
 * if IndexedDB is unavailable (private browsing, storage pressure, quota); the
 * map then simply loads tiles from the network as before.
 */

const DB_NAME = 'solar-basemap-tiles';
const DB_VERSION = 1;
const STORE_NAME = 'tiles';
const CREATED_INDEX = 'created';

/** Orthophotos are reshot yearly at best, so a month is comfortably fresh. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Roughly 75 MB at ~25 KB per orthophoto tile. */
const MAX_ENTRIES = 3000;

/** Pruning walks the whole store, so it runs rarely rather than on every write. */
const PRUNE_EVERY_N_WRITES = 200;

interface CachedTile {
  key: string;
  blob: Blob;
  created: number;
}

@Injectable({ providedIn: 'root' })
export class TileCacheService {

  private database: Promise<IDBDatabase | null> | null = null;
  private unavailable = false;
  private writesSincePrune = 0;

  /**
   * Returns the cached tile for a URL, or null on a miss or any storage error.
   */
  async get(key: string): Promise<Blob | null> {
    const db = await this.open();
    if (!db) return null;

    try {
      const record = await this.request<CachedTile | undefined>(
        db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key)
      );

      if (!record) return null;

      if (Date.now() - record.created > MAX_AGE_MS) {
        void this.delete(key);
        return null;
      }

      return record.blob;
    } catch {
      return null;
    }
  }

  /**
   * Stores a tile. Failures are swallowed — a cache miss is always survivable,
   * and a full disk should never break the map.
   */
  async put(key: string, blob: Blob): Promise<void> {
    const db = await this.open();
    if (!db) return;

    try {
      const entry: CachedTile = { key, blob, created: Date.now() };
      await this.request(
        db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(entry)
      );

      if (++this.writesSincePrune >= PRUNE_EVERY_N_WRITES) {
        this.writesSincePrune = 0;
        void this.prune();
      }
    } catch {
      // Most likely QuotaExceededError. Free space for the next write.
      this.writesSincePrune = 0;
      void this.prune();
    }
  }

  async delete(key: string): Promise<void> {
    const db = await this.open();
    if (!db) return;

    try {
      await this.request(
        db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(key)
      );
    } catch {
      // Nothing to recover — the entry stays until the next prune.
    }
  }

  /** Empties the cache. Exposed for a "clear offline maps" action. */
  async clear(): Promise<void> {
    const db = await this.open();
    if (!db) return;

    try {
      await this.request(
        db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).clear()
      );
    } catch {
      // Ignored — clearing is best effort.
    }
  }

  /** Tile count plus the browser's estimate of bytes used, for a settings view. */
  async stats(): Promise<{ tiles: number; bytes: number | null }> {
    const db = await this.open();
    let tiles = 0;

    if (db) {
      try {
        tiles = await this.request<number>(
          db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).count()
        );
      } catch {
        tiles = 0;
      }
    }

    let bytes: number | null = null;
    if (navigator.storage?.estimate) {
      try {
        bytes = (await navigator.storage.estimate()).usage ?? null;
      } catch {
        bytes = null;
      }
    }

    return { tiles, bytes };
  }

  /**
   * Drops expired tiles, then the oldest ones if the store is still over
   * budget. Cheap enough to run occasionally, too slow to run per write.
   */
  private async prune(): Promise<void> {
    const db = await this.open();
    if (!db) return;

    try {
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);
      const index = store.index(CREATED_INDEX);

      const expiredBefore = Date.now() - MAX_AGE_MS;
      let remaining = await this.request<number>(store.count());

      await new Promise<void>((resolve, reject) => {
        const cursorRequest = index.openCursor();

        cursorRequest.onerror = () => reject(cursorRequest.error);
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) {
            resolve();
            return;
          }

          // The index is ordered oldest first, so both conditions walk forward.
          const entry = cursor.value as CachedTile;
          if (entry.created < expiredBefore || remaining > MAX_ENTRIES) {
            cursor.delete();
            remaining--;
            cursor.continue();
            return;
          }

          resolve();
        };
      });
    } catch {
      // Pruning is opportunistic; the next write will try again.
    }
  }

  private open(): Promise<IDBDatabase | null> {
    if (this.unavailable) return Promise.resolve(null);
    if (this.database) return this.database;

    this.database = new Promise<IDBDatabase | null>(resolve => {
      if (typeof indexedDB === 'undefined') {
        this.unavailable = true;
        resolve(null);
        return;
      }

      let request: IDBOpenDBRequest;
      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch {
        this.unavailable = true;
        resolve(null);
        return;
      }

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex(CREATED_INDEX, 'created');
        }
      };

      request.onsuccess = () => resolve(request.result);

      request.onerror = () => {
        this.unavailable = true;
        resolve(null);
      };

      // Firefox in private mode never settles the request at all.
      request.onblocked = () => {
        this.unavailable = true;
        resolve(null);
      };
    });

    return this.database;
  }

  private request<T>(request: IDBRequest): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error);
    });
  }
}
